import CssBaseline from "@mui/material/CssBaseline";
import { Theme, ThemeProvider } from "@mui/material/styles";
import { Suspense, useEffect } from "react";
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
} from "react-router-dom";
import LoadingLogo from "../../components/LoadingLogo/LoadingLogo";
import {
  AuthContext,
  useAuthentication,
  useProvideAuth,
} from "../../hooks/Authentication";
import { ClientContext, useProvideClient } from "../../hooks/Client";
import { IClient } from "../../hooks/Client/ClientContext";
import {
  CuberTheme,
  GeniusTheme,
  GirasolTheme,
  HeliosTheme,
  RhodasTheme,
  GenerdisTheme,
  IbattmanTheme,
  PvwineTheme,
  CesamTheme,
  DigibatTheme,
  DigibatCppTheme,
} from "../../themes/";
import {
  LazyDashboardPage,
  LazyInstallationDetailPage,
  LazyInstallationPage,
  LazyLayoutPage,
  LazyLoginPage,
  LazyPreferencePage,
  LazyTablePage,
  LazyUserPage,
  LazyHistoricalImportPage,
} from "../";
import { Features } from "../../models/Features";
import { RoleType } from "../../models/IUser";

const PrivateRoute = ({ layout, children, ...rest }: any) => {
  const auth = useAuthentication();

  return (
    <Route
      {...rest}
      render={({ location }) => {
        if (auth.isAuthenticated) {
          return <LazyLayoutPage>{children}</LazyLayoutPage>;
        } else {
          return (
            <Redirect
              exact
              to={{
                pathname: "/login",
                state: { from: location },
              }}
            />
          );
        }
      }}
    />
  );
};

const App = () => {
  const authProvider = useProvideAuth();
  const clientProvider = useProvideClient();

  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  const getTheme: (client: IClient) => Theme = (client: IClient) => {
    switch (client.name) {
      case "Girasol":
        return GirasolTheme;
      case "Helios":
        return HeliosTheme;
      case "Genius":
        return GeniusTheme;
      case "RHODaS":
        return RhodasTheme;
      case "Generdis":
        return GenerdisTheme;
      case "iBattMan":
        return IbattmanTheme;
      case "PvWine":
        return PvwineTheme;
      case "Cesam":
        return CesamTheme;
      case "Digibat":
        return DigibatTheme;
      case "Digibat-Cpp":
        return DigibatCppTheme;
      default:
        return CuberTheme;
    }
  };

  return (
    <ClientContext.Provider value={clientProvider}>
      <AuthContext.Provider value={authProvider}>
        <ThemeProvider theme={getTheme(clientProvider.client)}>
          <CssBaseline />
          <Suspense fallback={<LoadingLogo />}>
            <Router>
              <Switch>
                <PrivateRoute exact path="/">
                  <LazyDashboardPage />
                </PrivateRoute>
                <Route path="/login">
                  <LazyLoginPage />
                </Route>
                <PrivateRoute path="/dashboard">
                  <LazyDashboardPage />
                </PrivateRoute>
                <PrivateRoute path="/users">
                  {authProvider.user && authProvider.user.role === RoleType.Administrator ? <LazyUserPage /> : <Redirect to="/dashboard" />}
                </PrivateRoute>
                <PrivateRoute exact path={`/${clientProvider.client.pagePath}`}>
                  {authProvider.user && (authProvider.user.role === RoleType.Administrator || authProvider.user.role === RoleType.Operator) ? <LazyInstallationPage /> : <Redirect to="/dashboard" />}
                </PrivateRoute>
                <PrivateRoute path={`/${clientProvider.client.pagePath}/:id`}>
                  <LazyInstallationDetailPage />
                </PrivateRoute>
                <PrivateRoute path="/historical-import/:id">
                  {authProvider.user && (authProvider.user.role === RoleType.Administrator || authProvider.user.role === RoleType.Operator) ? <LazyHistoricalImportPage /> : <Redirect to="/dashboard" />}
                </PrivateRoute>
                {clientProvider.client && clientProvider.client.features.some(
                  (c) => c === Features.Preferences
                ) && (
                    <>
                      <PrivateRoute path="/tables/:id">
                        {authProvider.user && authProvider.user.role === RoleType.Administrator ? <LazyTablePage /> : <Redirect to="/dashboard" />}
                      </PrivateRoute>
                      <PrivateRoute path="/preferences">
                        {authProvider.user && authProvider.user.role === RoleType.Administrator ? <LazyPreferencePage /> : <Redirect to="/dashboard" />}
                      </PrivateRoute>
                    </>
                  )}
              </Switch>
            </Router>
          </Suspense>
        </ThemeProvider>
      </AuthContext.Provider>
    </ClientContext.Provider>
  );
};

export default App;
